import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./ImageUpload.css";

const ImageUpload = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };
  const fileInputRef = useRef(null);

  // ─── State ──────────────────────────────────────────────────────────────────
  const [storageInfo, setStorageInfo] = useState(null);
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingInfo, setLoadingInfo] = useState(true);

  // Payment
  const [offers, setOffers] = useState([]);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Create folder inline
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderPassword, setNewFolderPassword] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);

  // ─── Auth check ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoadingInfo(true);
      const [infoRes, foldersRes, offersRes] = await Promise.all([
        axios.get("/api/images/storage-info", { headers }),
        axios.get("/api/images/folders", { headers }),
        axios.get("/api/payment/offers"),
      ]);
      if (infoRes.data.success) setStorageInfo(infoRes.data);
      if (foldersRes.data.success) setFolders(foldersRes.data.folders);
      if (offersRes.data.success) {
        // Only image offers
        const imageOffers = offersRes.data.offers.filter(
          (o) => o.title && o.title.toLowerCase().includes("image")
        );
        setOffers(imageOffers);
      }
    } catch (err) {
      console.error("Failed to load data:", err);
    } finally {
      setLoadingInfo(false);
    }
  };

  // ─── File selection ─────────────────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files).filter((f) =>
      f.type.startsWith("image/")
    );
    if (selected.length === 0) {
      alert("No image files found. Please select image files.");
      return;
    }
    if (selected.length > 500) {
      alert("You can upload at most 500 images at a time.");
      return;
    }
    setFiles(selected);
  };

  // Drop handler for folder drag-and-drop
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const items = e.dataTransfer.items;
    const imageFiles = [];

    const readEntry = (entry) => {
      return new Promise((resolve) => {
        if (entry.isFile) {
          entry.file((file) => {
            if (file.type.startsWith("image/")) imageFiles.push(file);
            resolve();
          });
        } else if (entry.isDirectory) {
          const reader = entry.createReader();
          reader.readEntries(async (entries) => {
            for (const e of entries) {
              await readEntry(e);
            }
            resolve();
          });
        } else {
          resolve();
        }
      });
    };

    for (const item of items) {
      const entry = item.webkitGetAsEntry && item.webkitGetAsEntry();
      if (entry) {
        await readEntry(entry);
      }
    }

    if (imageFiles.length === 0) {
      alert("No image files found in the dropped items.");
      return;
    }
    if (imageFiles.length > 500) {
      alert(`Found ${imageFiles.length} images. Maximum is 500. Only first 500 will be used.`);
      imageFiles.splice(500);
    }
    setFiles(imageFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // ─── Upload ─────────────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (files.length === 0) {
      alert("Please select images first.");
      return;
    }
    if (!selectedFolderId) {
      alert("Please select a folder first.");
      return;
    }

    setUploading(true);
    setUploadedCount(0);
    setTotalCount(files.length);

    // Upload in batches of 10 to avoid huge payloads
    const batchSize = 10;
    let uploaded = 0;

    try {
      for (let i = 0; i < files.length; i += batchSize) {
        const batch = files.slice(i, i + batchSize);
        const formData = new FormData();
        formData.append("folderId", selectedFolderId);
        batch.forEach((f) => formData.append("images", f));

        await axios.post("/api/images/upload", formData, {
          headers: {
            ...headers,
            "Content-Type": "multipart/form-data",
          },
          timeout: 300000, // 5 min timeout per batch
        });

        uploaded += batch.length;
        setUploadedCount(uploaded);
      }

      alert(`✅ Successfully uploaded ${uploaded} images!`);
      setFiles([]);
      fetchData(); // refresh counts
    } catch (err) {
      const errMsg = err.response?.data?.error || "Upload failed.";
      if (errMsg === "IMAGE_STORAGE_NOT_PURCHASED") {
        alert("Please purchase image storage before uploading.");
      } else {
        alert(errMsg);
      }
    } finally {
      setUploading(false);
    }
  };

  // ─── Create folder ──────────────────────────────────────────────────────────
  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim() || !newFolderPassword.trim()) return;
    try {
      setCreatingFolder(true);
      const res = await axios.post("/api/images/create-folder", {
        name: newFolderName.trim(),
        password: newFolderPassword.trim(),
      }, { headers });
      if (res.data.success) {
        setShowNewFolder(false);
        setNewFolderName("");
        setNewFolderPassword("");
        setSelectedFolderId(res.data.folder._id);
        fetchData();
      }
    } catch (err) {
      alert(err.response?.data?.error || "Failed to create folder");
    } finally {
      setCreatingFolder(false);
    }
  };

  // ─── Payment ────────────────────────────────────────────────────────────────
  const handlePayment = async (offer) => {
    if (!window.Razorpay) {
      alert("Razorpay SDK not loaded. Please refresh the page.");
      return;
    }
    try {
      setProcessingPayment(true);
      const orderRes = await axios.post("/api/payment/create-order", { offerId: offer._id }, { headers });
      const { order, key } = orderRes.data;
      const options = {
        key,
        amount: order.amount,
        currency: order.currency,
        name: "Golden Memory",
        description: offer.description,
        order_id: order.id,
        handler: async (response) => {
          try {
            const verifyRes = await axios.post("/api/payment/verify-payment", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }, { headers });
            if (verifyRes.data.success) {
              alert("🎉 Payment successful! Image storage unlocked!");
              fetchData();
            }
          } catch (error) {
            alert("Payment verification failed. Contact support.");
          } finally {
            setProcessingPayment(false);
          }
        },
        theme: { color: "#667eea" },
        modal: {
          ondismiss: () => setProcessingPayment(false),
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      alert("Failed to initiate payment.");
      setProcessingPayment(false);
    }
  };

  // ─── Helpers ────────────────────────────────────────────────────────────────
  const formatMB = (bytes) => {
    if (!bytes) return "0 MB";
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ─── RENDER ─────────────────────────────────────────────────────────────────
  if (loadingInfo) {
    return (
      <div className="imgup-loading">
        <div className="imgup-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const storagePurchased = storageInfo && storageInfo.imageStorageLimit > 0;

  return (
    <div className="imgup-container">
      <div className="imgup-header">
        <h2>🖼️ Upload Images</h2>
      </div>

      {/* Storage Info */}
      {storagePurchased && storageInfo && (
        <div className="imgup-storage-bar">
          <div className="imgup-storage-stats">
            <span>
              {formatMB(storageInfo.usedImageStorage)} / {formatMB(storageInfo.imageStorageLimit)} used
            </span>
            <span>
              {storageInfo.imageCount} / {storageInfo.maxImages} images
            </span>
          </div>
          <div className="imgup-progress-bg">
            <div
              className="imgup-progress-fill"
              style={{
                width: `${Math.min((storageInfo.usedImageStorage / storageInfo.imageStorageLimit) * 100, 100)}%`,
              }}
            ></div>
          </div>
        </div>
      )}

      {/* Payment gate */}
      {!storagePurchased && (
        <div className="imgup-payment-gate">
          <div className="imgup-payment-card">
            <div className="imgup-payment-header">
              <h3>🔓 Unlock Image Storage</h3>
              <p>Purchase image storage to start uploading high-quality photos.</p>
            </div>
            {offers.map((offer) => (
              <div key={offer._id} className="imgup-offer">
                <div className="imgup-offer-info">
                  <h4>{offer.title}</h4>
                  <p>{offer.description}</p>
                  <ul>
                    {offer.features && offer.features.map((f, i) => (
                      <li key={i}>✓ {f}</li>
                    ))}
                  </ul>
                </div>
                <div className="imgup-offer-price">
                  <span className="imgup-original-price">₹{Math.round(offer.price * 100)}</span>
                  <span className="imgup-discounted-price">₹{offer.price}</span>
                  <span className="imgup-discount-badge">99% OFF</span>
                </div>
                <button
                  className="imgup-pay-btn"
                  onClick={() => handlePayment(offer)}
                  disabled={processingPayment}
                >
                  {processingPayment ? "Processing..." : `Pay ₹${offer.price}`}
                </button>
              </div>
            ))}
            {offers.length === 0 && (
              <p className="imgup-no-offer">Image storage offer not available yet. Please contact support.</p>
            )}
          </div>
        </div>
      )}

      {/* Upload UI – only show if purchased */}
      {storagePurchased && (
        <>
          {/* Folder selection */}
          <div className="imgup-folder-select">
            <label>Select Folder:</label>
            <div className="imgup-folder-row">
              <select
                value={selectedFolderId}
                onChange={(e) => setSelectedFolderId(e.target.value)}
              >
                <option value="">-- Choose a folder --</option>
                {folders.map((f) => (
                  <option key={f._id} value={f._id}>{f.name}</option>
                ))}
              </select>
              <button className="imgup-new-folder-btn" onClick={() => setShowNewFolder(true)}>
                + New Folder
              </button>
            </div>
          </div>

          {/* Inline create folder */}
          {showNewFolder && (
            <form className="imgup-new-folder-form" onSubmit={handleCreateFolder}>
              <input
                type="text"
                placeholder="Folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                required
              />
              <input
                type="password"
                placeholder="Download password"
                value={newFolderPassword}
                onChange={(e) => setNewFolderPassword(e.target.value)}
                required
              />
              <button type="submit" disabled={creatingFolder}>
                {creatingFolder ? "Creating..." : "Create"}
              </button>
              <button type="button" onClick={() => setShowNewFolder(false)}>Cancel</button>
            </form>
          )}

          {/* Drop zone */}
          <div
            className="imgup-dropzone"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              accept="image/*"
              multiple
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: "none" }}
              // Allow folder selection via webkitdirectory
            />
            <div className="imgup-dropzone-content">
              <span className="imgup-dropzone-icon">📁</span>
              <h3>Click to select images or drag & drop a folder</h3>
              <p>Max 500 images · JPG, PNG, WEBP, HEIC supported · Original quality preserved</p>
            </div>
          </div>

          {/* Selected files count */}
          {files.length > 0 && !uploading && (
            <div className="imgup-selected-info">
              <p>🖼️ {files.length} image{files.length > 1 ? "s" : ""} selected</p>
              <div className="imgup-actions">
                <button className="imgup-upload-btn" onClick={handleUpload}>
                  Upload {files.length} Image{files.length > 1 ? "s" : ""}
                </button>
                <button className="imgup-clear-btn" onClick={() => setFiles([])}>
                  Clear
                </button>
              </div>
            </div>
          )}

          {/* Upload progress */}
          {uploading && (
            <div className="imgup-upload-progress">
              <div className="imgup-progress-label">
                Uploading: {uploadedCount} / {totalCount} images
              </div>
              <div className="imgup-progress-bg large">
                <div
                  className="imgup-progress-fill"
                  style={{ width: `${(uploadedCount / totalCount) * 100}%` }}
                ></div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ImageUpload;
